import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LanguageCode, languages, defaultLanguage, isRTL, getLanguageByCode } from './config';
import { translations, TranslationKeys, LocalizedTranslations } from './translations';

interface I18nContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  t: LocalizedTranslations;
  isRTL: boolean;
  languages: typeof languages;
  getLocalizedPath: (path: string) => string;
  getCurrentLanguageInfo: () => typeof languages[0];
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const STORAGE_KEY = 'aureum-language';

// Extract language code from path
const getLanguageFromPath = (pathname: string): LanguageCode | null => {
  const match = pathname.match(/^\/([a-z]{2})(\/|$)/);
  if (match) {
    const code = match[1] as LanguageCode;
    if (languages.some(l => l.code === code)) {
      return code;
    }
  }
  return null;
};

// Remove language prefix from path
const removeLanguagePrefix = (pathname: string): string => {
  return pathname.replace(/^\/[a-z]{2}(\/|$)/, '/');
};

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize language from URL, storage, or browser
  const getInitialLanguage = (): LanguageCode => {
    // First check URL
    const pathLang = getLanguageFromPath(location.pathname);
    if (pathLang) return pathLang;
    
    // Then check storage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && languages.some(l => l.code === stored)) {
      return stored as LanguageCode;
    }
    
    // Finally check browser preference
    const browserLang = navigator.language.split('-')[0] as LanguageCode;
    if (languages.some(l => l.code === browserLang)) {
      return browserLang;
    }
    
    return defaultLanguage;
  };

  const [language, setLanguageState] = useState<LanguageCode>(getInitialLanguage);

  // Update HTML attributes for RTL
  useEffect(() => {
    const lang = getLanguageByCode(language);
    document.documentElement.lang = lang?.locale || 'en';
    document.documentElement.dir = lang?.direction || 'ltr';
    
    // Add/remove RTL class for styling
    if (isRTL(language)) {
      document.documentElement.classList.add('rtl');
    } else {
      document.documentElement.classList.remove('rtl');
    }
  }, [language]);

  // Sync language with URL
  useEffect(() => {
    const pathLang = getLanguageFromPath(location.pathname);
    if (pathLang && pathLang !== language) {
      setLanguageState(pathLang);
      localStorage.setItem(STORAGE_KEY, pathLang);
    }
  }, [location.pathname, language]);

  const setLanguage = useCallback((newLang: LanguageCode) => {
    if (newLang === language) return;
    
    setLanguageState(newLang);
    localStorage.setItem(STORAGE_KEY, newLang);
    
    // Update URL with new language prefix
    const currentPath = removeLanguagePrefix(location.pathname);
    const newPath = `/${newLang}${currentPath === '/' ? '' : currentPath}`;
    navigate(newPath, { replace: true });
  }, [language, location.pathname, navigate]);

  const getLocalizedPath = useCallback((path: string): string => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `/${language}${cleanPath === '/' ? '' : cleanPath}`;
  }, [language]);

  const getCurrentLanguageInfo = useCallback(() => {
    return languages.find(l => l.code === language) || languages[0];
  }, [language]);

  const value: I18nContextType = {
    language,
    setLanguage,
    t: translations[language],
    isRTL: isRTL(language),
    languages,
    getLocalizedPath,
    getCurrentLanguageInfo,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

// Hook for getting translated text with fallback
export const useTranslation = () => {
  const { t, language } = useI18n();
  return { t, language };
};
